import React, { useState, useEffect, useCallback } from "react";
import apiClient from "@/api/client";
import { useParams, useNavigate } from "react-router-dom";
import { MemberHeader } from "@/components/member-detail/MemberHeader";
import { ExceptionSection } from "@/components/member-detail/ExceptionSection";
import { SubscriptionList } from "@/components/member-detail/SubscriptionCard";
import { ActivityTimeline } from "@/components/member-detail/ActivityTimeline";
import {
  AddSubscriptionDialog, RenewalDialog, EditMemberDialog,
  SuspensionDialog, EditSubscriptionDialog, DeleteMemberDialog,
} from "@/components/member-detail/MemberDialogs";

const MemberDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [subs, setSubs] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [suspensions, setSuspensions] = useState({});
  const [activities, setActivities] = useState([]);
  const [resending, setResending] = useState(false);

  // Dialog visibility
  const [showAddSub, setShowAddSub] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showEditSub, setShowEditSub] = useState(false);
  const [showRenewal, setShowRenewal] = useState(false);
  const [showSuspension, setShowSuspension] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Form state
  const [suspSubId, setSuspSubId] = useState("");
  const [suspForm, setSuspForm] = useState({ suspension_start: "", suspension_end: "", reason: "MEDICAL" });
  const [editForm, setEditForm] = useState({});
  const [editSubForm, setEditSubForm] = useState({});
  const [renewalForm, setRenewalForm] = useState({});
  const [subForm, setSubForm] = useState({ plan_name: "", plan_category: "STANDARD", price_eur: 0, payment_type: "CARD", start_date: new Date().toISOString().slice(0, 10) });

  // Exception inline editing
  const [editingException, setEditingException] = useState(false);
  const [exceptionText, setExceptionText] = useState("");

  // ─── Data fetching ─────────────────────────────────────────────────────────
  const fetchMember = useCallback(async () => {
    try {
      const r = await apiClient.get(`/members/${id}`);
      setMember(r.data.member);
      setSubs(r.data.subscriptions || []);
      setEditForm(r.data.member);
    } catch { navigate("/members"); }
  }, [id, navigate]);

  const fetchActivities = useCallback(async () => {
    try {
      const res = await apiClient.get("/activities", { params: { member_id: id } });
      setActivities(res.data);
    } catch (e) { console.error(e); }
  }, [id]);

  const refreshSubs = useCallback(async () => {
    const res = await apiClient.get("/subscriptions", { params: { member_id: id } });
    setSubs(res.data);
    const suspMap = {};
    for (const s of res.data) {
      const suspRes = await apiClient.get("/suspensions", { params: { subscription_id: s.id } });
      suspMap[s.id] = suspRes.data;
    }
    setSuspensions(suspMap);
  }, [id]);

  useEffect(() => {
    fetchMember();
    apiClient.get("/catalog").then(r => setCatalog(r.data)).catch(console.error);
  }, [fetchMember]);

  useEffect(() => {
    refreshSubs();
    fetchActivities();
  }, [refreshSubs, fetchActivities]);

  const refreshAll = async () => {
    const r = await apiClient.get(`/members/${id}`);
    setMember(r.data.member);
    setEditForm(r.data.member);
    fetchActivities();
  };

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleAddSuspension = async () => {
    if (!suspForm.suspension_start || !suspForm.suspension_end) return;
    await apiClient.post("/suspensions", { subscription_id: suspSubId, ...suspForm });
    setShowSuspension(false);
    setSuspForm({ suspension_start: "", suspension_end: "", reason: "MEDICAL" });
    await refreshSubs();
    await refreshAll();
  };

  const handleDeleteSuspension = async (suspId) => {
    if (!window.confirm("Supprimer cette suspension ?")) return;
    await apiClient.delete(`/suspensions/${suspId}`);
    await refreshSubs();
    fetchActivities();
  };

  const handleSaveMember = async () => {
    await apiClient.put(`/members/${id}`, { last_name: editForm.last_name, first_name: editForm.first_name, email: editForm.email, phone: editForm.phone, notes: editForm.notes, status: editForm.status });
    setShowEdit(false);
    await refreshAll();
  };

  const handleAddSub = async () => {
    await apiClient.post("/subscriptions", { member_id: id, ...subForm, price_eur: Number(subForm.price_eur), is_auto_renew: subForm.payment_type === "DOMICILIATION" });
    await refreshSubs();
    setShowAddSub(false);
    fetchActivities();
  };

  const handleSelectPlan = (planId) => {
    const plan = catalog.find(c => c.id === planId);
    if (plan) setSubForm({ ...subForm, plan_name: plan.name, plan_category: plan.category, price_eur: plan.price, payment_type: plan.payment_type === "DOMICILIATION" ? "DOMICILIATION" : subForm.payment_type });
  };

  const handleDeleteSub = async (subId) => {
    if (!window.confirm("Supprimer cet abonnement ?")) return;
    await apiClient.delete(`/subscriptions/${subId}`);
    await refreshSubs();
    fetchActivities();
  };

  const handleNotRenewed = async (subId) => {
    await apiClient.put(`/subscriptions/${subId}`, { renewal_decision: "NOT_RENEWED" });
    await refreshSubs();
    await refreshAll();
  };

  const openRenewalModal = (s) => {
    const endDate = (s.end_date_effective || s.end_date || "").slice(0, 10);
    setRenewalForm({ old_sub_id: s.id, plan_name: s.plan_name || "", plan_category: s.plan_category || "STANDARD", price_eur: s.price_eur || 0, payment_type: s.payment_type || "CARD", start_date: endDate || new Date().toISOString().slice(0, 10) });
    setShowRenewal(true);
  };

  const handleSelectRenewalPlan = (planId) => {
    const plan = catalog.find(c => c.id === planId);
    if (plan) setRenewalForm({ ...renewalForm, plan_name: plan.name, plan_category: plan.category, price_eur: plan.price, payment_type: plan.payment_type === "DOMICILIATION" ? "DOMICILIATION" : renewalForm.payment_type });
  };

  const handleSubmitRenewal = async () => {
    await apiClient.put(`/subscriptions/${renewalForm.old_sub_id}`, { renewal_decision: "RENEWED" });
    await apiClient.post("/subscriptions", { member_id: id, plan_name: renewalForm.plan_name, plan_category: renewalForm.plan_category, price_eur: Number(renewalForm.price_eur), payment_type: renewalForm.payment_type, start_date: renewalForm.start_date, is_auto_renew: renewalForm.payment_type === "DOMICILIATION" });
    await apiClient.put(`/members/${id}`, { status: "ACTIVE" });
    setShowRenewal(false);
    await refreshSubs();
    await refreshAll();
  };

  const openEditSub = (s) => {
    setEditSubForm({ id: s.id, plan_name: s.plan_name || "", plan_category: s.plan_category || "STANDARD", price_eur: s.price_eur || 0, payment_type: s.payment_type || "CARD", start_date: s.start_date ? s.start_date.slice(0, 10) : "", end_date: (s.end_date || "").slice(0, 10) });
    setShowEditSub(true);
  };

  const handleSaveEditSub = async () => {
    await apiClient.put(`/subscriptions/${editSubForm.id}`, { plan_name: editSubForm.plan_name, plan_category: editSubForm.plan_category, price_eur: Number(editSubForm.price_eur), payment_type: editSubForm.payment_type, start_date: editSubForm.start_date, end_date: editSubForm.end_date, subscription_status: editSubForm.subscription_status });
    setShowEditSub(false);
    await refreshSubs();
    await refreshAll();
  };

  const toggleSubStatus = async (sub) => {
    await apiClient.put(`/subscriptions/${sub.id}`, { subscription_status: sub.is_active ? "INACTIVE" : "ACTIVE" });
    await refreshSubs();
    fetchActivities();
  };

  const handleMarkFormer = async () => {
    await apiClient.put(`/members/${id}`, { status: "FORMER" });
    await refreshAll();
  };

  const handleSaveException = async () => {
    await apiClient.put(`/members/${id}`, { exception: exceptionText });
    await refreshAll();
    setEditingException(false);
  };

  const handleDeleteMember = async () => {
    try { await apiClient.delete(`/members/${id}`); navigate("/members"); }
    catch (err) { console.error("Error deleting member:", err); }
  };

  const handleResendEmail = async () => {
    if (!member.email) return alert("Ce membre n'a pas d'adresse email");
    setResending(true);
    try {
      await apiClient.post(`/members/${id}/resend-email`);
      await refreshAll();
      alert("Email envoyé avec succès !");
    } catch (err) {
      alert("Échec de l'envoi : " + (err.response?.data?.detail || err.message));
    } finally { setResending(false); }
  };

  if (!member) return <div className="crm-page-loading">Chargement...</div>;

  return (
    <div className="crm-page">
      <MemberHeader
        member={member} resending={resending}
        onBack={() => navigate("/members")} onEdit={() => setShowEdit(true)}
        onMarkFormer={handleMarkFormer} onDelete={() => setShowDeleteConfirm(true)}
        onResendEmail={handleResendEmail}
      />

      <ExceptionSection
        member={member} editing={editingException} exceptionText={exceptionText}
        onStartEdit={() => { setEditingException(true); setExceptionText(member.exception || ""); }}
        onSave={handleSaveException} onCancel={() => setEditingException(false)}
        onTextChange={setExceptionText}
      />

      <SubscriptionList
        subs={subs} suspensions={suspensions} onAdd={() => setShowAddSub(true)}
        onEdit={openEditSub} onDelete={handleDeleteSub} onToggleStatus={toggleSubStatus}
        onRenew={openRenewalModal} onNotRenewed={handleNotRenewed}
        onAddSuspension={(subId) => { setSuspSubId(subId); setShowSuspension(true); }}
        onDeleteSuspension={handleDeleteSuspension}
      />

      <ActivityTimeline activities={activities} />

      <AddSubscriptionDialog open={showAddSub} onOpenChange={setShowAddSub} catalog={catalog} form={subForm} setForm={setSubForm} onSelectPlan={handleSelectPlan} onSubmit={handleAddSub} />
      <RenewalDialog open={showRenewal} onOpenChange={setShowRenewal} catalog={catalog} form={renewalForm} setForm={setRenewalForm} onSelectPlan={handleSelectRenewalPlan} onSubmit={handleSubmitRenewal} />
      <EditMemberDialog open={showEdit} onOpenChange={setShowEdit} form={editForm} setForm={setEditForm} onSave={handleSaveMember} />
      <SuspensionDialog open={showSuspension} onOpenChange={setShowSuspension} form={suspForm} setForm={setSuspForm} onSubmit={handleAddSuspension} />
      <EditSubscriptionDialog open={showEditSub} onOpenChange={setShowEditSub} form={editSubForm} setForm={setEditSubForm} onSave={handleSaveEditSub} />
      <DeleteMemberDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm} member={member} onConfirm={handleDeleteMember} />
    </div>
  );
};

export default MemberDetailPage;
